import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTabDraft } from "@/components/ui/box-draft";
import { Button } from "@/components/ui/button";
import type { Box, Result, TaskBox } from "@/components/ui/box.schema";
import { enrollmentsApi, type EnrollmentNotice } from "@/lib/api/enrollments";
import { checkCommit } from "@/lib/api/commitSeams";
import { keepResult, recorded, registerCommit, registerRead, noticeCopy, registerText } from "@/lib/register-copy";

/** The roster supplies this box to its one Bento and one ranking scope. */
export function useEnrollmentNoticeRecovery({ noticeId, courseId, onClose }: {
  noticeId: string | null; courseId: string; onClose: () => void;
}): Box {
  const client = useQueryClient();
  useEffect(() => {
    if (!noticeId) return;
    const heading = document.querySelector<HTMLElement>('[data-box-id="enrollment-notice"] h2');
    if (heading) { heading.tabIndex = -1; heading.focus(); }
  }, [noticeId]);
  const notice = useQuery({
    queryKey: ["enrollment-notice", courseId, noticeId],
    queryFn: () => enrollmentsApi.notice(noticeId!),
    enabled: Boolean(noticeId), retry: false,
  });
  const keep = useTabDraft<null, string>({ box: "enrollment-notice", record: `${courseId}/${noticeId ?? "none"}`, version: notice.data?.course_id === courseId ? notice.data.updated_at ?? null : null, initial: null });
  const pending = keep.pending;
  const setPending = keep.setPending;
  const [held, setHeld] = useState<{ id: string; result: Result } | null>(null);
  const sending = useRef(new Set<string>());
  const heldResult = held?.id === noticeId ? held.result : { state: "none" } as Result;
  const current = notice.data?.course_id === courseId ? notice.data : undefined;
  const store = (value: Result) => {
    setHeld({ id: noticeId!, result: value });
    if (value.state === "confirmed" || value.state === "refused") setPending(null);
  };
  const read = async () => {
    const value = await enrollmentsApi.notice(noticeId!);
    if (value.course_id !== courseId) throw new Error(noticeCopy.unavailable);
    client.setQueryData(["enrollment-notice", courseId, noticeId], value);
    return value;
  };
  const settle = (value: EnrollmentNotice, key: string): Result => {
    if (value.state === "accepted") return recorded([noticeCopy.accepted]);
    if (value.state === "failed" || value.state === "not_configured")
      return { state: "refused", cap: noticeCopy.notSent, sentence: value.reason || noticeCopy.states[value.state].detail, draft: "kept" };
    return unknown(key);
  };
  const unknown = (key: string): Result => ({
    state: "unknown", cap: noticeCopy.notConfirmed, requestId: key,
    sentence: noticeCopy.unknown,
    check: { verb: noticeCopy.check, note: noticeCopy.checkNote, run: async () => {
      let next: Result;
      try {
        const checked = await checkCommit(key, (value) => value);
        next = checked.state === "not_committed"
          ? { state: "refused", cap: noticeCopy.notSent, sentence: noticeCopy.notRecorded, draft: "kept" }
          : settle(await read(), key);
      } catch { next = unknown(key); }
      return keepResult(next, store);
    } },
  });
  const result = heldResult.state === "none" && pending && !sending.current.has(pending) ? unknown(pending) : heldResult;
  const available = Boolean(current?.updated_at && current.retry_eligible && ["pending", "failed", "not_configured"].includes(current.state));
  const reason = !current ? noticeCopy.unavailable
    : !current.updated_at ? noticeCopy.readVersion
    : !available ? noticeCopy.checkBeforeSend : null;
  const box: TaskBox<EnrollmentNotice> = {
    kind: "task", id: "enrollment-notice", job: registerText.sendOrCheckRecordedNotice,
    label: noticeCopy.title, owner: { system: "imam" }, fold: "never",
    read: !noticeId ? { status: "unread", sentence: noticeCopy.choose } : (notice.data && !current) || (notice.isError && !notice.data) ? { status: "failed", sentence: noticeCopy.unavailable, retry: () => void notice.refetch() }
      : registerRead(notice, noticeCopy.title),
    detail: (value) => noticeCopy.states[value.state].detail,
    state: (value) => ({ class: value.state === "accepted" ? "done" : value.state === "failed" || value.state === "not_configured" ? "failed" : "waiting", word: noticeCopy.states[value.state].label }),
    act: !noticeId ? undefined : available ? { does: "open", verb: current?.state === "pending" ? noticeCopy.send : noticeCopy.retry }
      : { does: "check", verb: noticeCopy.check, run: () => void notice.refetch() },
    // Unranked until the server includes this exact notice in its projection.
    candidate: { eligible: available, rank: null, blocked: null },
    draft: result.state === "unknown", result,
    parts: [{ id: "notice-record", label: noticeCopy.record, owner: { system: "imam" }, fold: "never", draft: "browser", keep, markedByBox: true,
      state: { class: "quiet", word: current ? noticeCopy.states[current.state].label : noticeCopy.unavailable },
      render: () => <div className="grid justify-items-start gap-2">
        {current && <>
          <p>{noticeCopy.states[current.state].detail}</p>
          {(current.learner_name || current.schedule_label) && <p>{[current.learner_name, current.schedule_label].filter(Boolean).join(" · ")}</p>}
          {current.recipient_source && <p>{noticeCopy.contact(current.recipient_source)}</p>}
          {current.reason && <p>{noticeCopy.reason(current.reason)}</p>}
        </>}
        {result.state !== "unknown" && <Button type="button" variant="outline" onClick={() => void notice.refetch()}>{noticeCopy.check}</Button>}
        <Button type="button" variant="ghost" onClick={onClose}>{noticeCopy.back}</Button>
      </div>,
    }],
    commit: { verb: current?.state === "pending" ? noticeCopy.send : noticeCopy.retry,
      cap: noticeCopy.when, effects: () => [{ id: "notice-record", sentence: noticeCopy.effect }],
      blockers: () => reason ? [{ kind: "missing", part: "notice-record", sentence: reason }] : [],
      run: async (key) => {
        sending.current.add(key);
        setPending(key);
        try {
          const next = await registerCommit(key,
            // The timestamp is an opaque version, including its microseconds.
            () => enrollmentsApi.retryNotice(noticeId!, { key, version: current!.updated_at! }),
            (value) => { client.setQueryData(["enrollment-notice", courseId, noticeId], value); return settle(value, key); },
          );
          return keepResult(next.state === "unknown" ? unknown(key) : next, store);
        } finally { sending.current.delete(key); }
      },
    },
  };
  return box as Box;
}
